<template>
  <div class="seller-account-details-wrapper">
    <!-- Floating Navigation Menu -->
    <nav class="section-nav">
      <ul class="section-nav__list">
        <li
          v-for="item in navigationItems"
          :key="item.id"
          class="section-nav__item"
        >
          <a
            :href="`#${item.id}`"
            class="section-nav__link"
            @click.prevent="navigateToSection(item.id)"
          >
            <CIcon
              :name="item.icon"
              size="sm"
              class="mr-1"
            />
            {{ item.label }}
          </a>
        </li>
      </ul>
    </nav>

    <!-- Main Content -->
    <div class="seller-account-details-content">
      <CCard
        v-if="getSafe(() => sellerAccountDetails.testOnly)"
        class="test-account-banner bg-danger p-3 text-white"
      >
        <strong>
          <CIcon
            name="cilWarning"
            class="mr-1"
          />
          This is a test-only account
        </strong>
      </CCard>

      <CCard
        v-if="getSafe(() => isAdminApprovalRequirementNotMet)"
        class="test-account-banner bg-warning px-3 py-3 text-white mb-3"
        :class="{
          'test-banner--position-2': getSafe(() => sellerAccountDetails.testOnly)
        }"
      >
        <strong class="d-flex align-items-center">
          <CIcon
            name="cilWarning"
            class="mr-1"
          />
          Admin Approval is pending
          <CButton
            color="success"
            class="ml-auto"
            size="sm"
            @click="warningApproveModal = true"
          >
            Approve
          </CButton>
        </strong>

        <h5
          v-if="sellerAccountDetails && sellerAccountDetails.nextPendingItemForFinalApproval"
          class="mb-0"
        >
          <i><small>Next Pending Item for Final Approval:</small> {{ sellerAccountDetails && sellerAccountDetails.nextPendingItemForFinalApproval }}</i>
        </h5>
      </CCard>

      <CCard id="section-account-details">
        <CCardHeader>
          <div class="d-flex justify-content-between">
            <div class="d-flex align-items-center">
              <CIcon
                name="cil-user"
                class="mr-2"
              />
              <strong>Account Details</strong>
            </div>

            <div class="d-flex align-items-center">
              <CDropdown
                v-if="sellerAccountDetails && sellerAccountDetails.enabled === true"
                class="d-inline-block mr-2"
                color="warning"
                size="sm"
              >
                <template #toggler-content>
                  <span>Disable</span>
                </template>
                <CDropdownItem @click="showDisableAccountModal('Paused')">
                  Pause
                </CDropdownItem>
                <CDropdownItem @click="showDisableAccountModal('Deactivated')">
                  Deactivate
                </CDropdownItem>
                <CDropdownItem @click="showDisableAccountModal('Banned')">
                  Ban
                </CDropdownItem>
              </CDropdown>

              <CButton
                v-if="sellerAccountDetails && sellerAccountDetails.enabled === false"
                color="success"
                size="sm"
                class="mr-2"
                :disabled="sellerAccountDetails.disableReason === 'Banned'"
                @click="showEnableAccountModal"
              >
                Enable
              </CButton>

              <CButton
                size="sm"
                :color="editMode ? 'danger' : 'primary'"
                @click="toggleEditMode"
              >
                {{ editMode ? 'Cancel' : 'Edit Details' }}
              </CButton>
            </div>
          </div>
        </CCardHeader>

        <CCardBody>
          <div v-if="sellerAccountDetails && sellerAccountDetails.id">
            <div>
              <div v-if="!editMode">
                <AccountDetailsCommonInfoReadOnly
                  type="seller"
                  :account-details="sellerAccountDetails"
                />
              </div>
              <div v-else>
                <AccountDetailsCommonInfoEdit
                  type="seller"
                  :account-details="sellerAccountDetails"
                  @updated="toggleEditMode"
                />
              </div>
            </div>

            <SellerBankInfo
              v-if="!editMode"
              :seller-account-details="sellerAccountDetails"
            />

            <div v-if="!editMode">
              <h3>
                Other Details
              </h3>

              <dl class="row">
                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Time Zone
                </dt>
                <dd
                  class="col-sm-9 col-12"
                >
                  {{ getSafe(() => timezonesMap[sellerAccountDetails.timeZoneId].label) }}
                </dd>

                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Categories
                </dt>
                <dd
                  class="col-sm-9 col-12"
                >
                  <span
                    v-for="(category, $idx) in sellerAccountDetails.sellerRegistrationDetails.categories"
                    :key="category.id"
                  >
                    {{ getSafe(() => categoriesMap[category.id].name) }}
                    <span
                      v-if="$idx !== sellerAccountDetails.sellerRegistrationDetails.categories.length - 1"
                      class="mx-1"
                    >|</span>
                  </span>
                </dd>

                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Inbound Traffic Sources
                </dt>
                <dd
                  class="col-sm-9 col-12"
                >
                  <span
                    v-for="(ts, $idx) in sellerAccountDetails.sellerRegistrationDetails.inboundTrafficSources"
                    :key="ts.id"
                  >
                    {{ getSafe(() => trafficSourcesMap[ts.id].name) }}
                    <span
                      v-if="$idx !== sellerAccountDetails.sellerRegistrationDetails.inboundTrafficSources.length - 1"
                      class="mx-1"
                    >|</span>
                  </span>
                </dd>

                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Outbound Traffic Sources
                </dt>

                <dd
                  class="col-sm-9 col-12"
                >
                  <span
                    v-for="(ts, $idx) in sellerAccountDetails.sellerRegistrationDetails.outboundTrafficSources"
                    :key="ts.id"
                  >
                    {{ getSafe(() => trafficSourcesMap[ts.id].name) }}
                    <span
                      v-if="$idx !== sellerAccountDetails.sellerRegistrationDetails.outboundTrafficSources.length - 1"
                      class="mx-1"
                    >|</span>
                  </span>
                </dd>

                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Transfer Traffic Sources
                </dt>

                <dd
                  class="col-sm-9 col-12"
                >
                  <span
                    v-for="(ts, $idx) in sellerAccountDetails.sellerRegistrationDetails.transferTrafficSources"
                    :key="ts.id"
                  >
                    {{ getSafe(() => trafficSourcesMap[ts.id].name) }}
                    <span
                      v-if="$idx !== sellerAccountDetails.sellerRegistrationDetails.transferTrafficSources.length - 1"
                      class="mx-1"
                    >|</span>
                  </span>
                </dd>


                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Monthly Calls Generated
                </dt>
                <dd
                  class="col-sm-9 col-12"
                >
                  {{ sellerAccountDetails.sellerRegistrationDetails.monthlyCallsGeneratedRange }}
                </dd>

                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Annual PPC Revenue
                </dt>
                <dd
                  class="col-sm-9 col-12"
                >
                  {{ sellerAccountDetails.sellerRegistrationDetails.annualPPCRevenueRange }}
                </dd>

                <dt
                  class="col-12 col-sm-3 text-sm-right"
                >
                  Works with Affiliates
                </dt>
                <dd
                  class="col-sm-9 col-12"
                >
                  {{ sellerAccountDetails.sellerRegistrationDetails.worksWithAffiliates ? 'Yes' : 'No' }}
                </dd>
              </dl>
            </div>
          </div>
        
          <SignedDocumentBundles
            account-type="seller"
            :account-id="getSafe(() => sellerAccountDetails.id)"
            :signed-bundles="getSafe(() => signedBundles)"
          />

          <AccountRequirements
            class="mt-4"
            :account-requirements="sellerAccountRequirements"
          />
        </CCardBody>
      </CCard>

      <div id="section-billing">
        <SellerBillingDetails
          :account-id="getSafe(() => accountId)"
          :account-billing-config="getSafe(() => sellerAccountDetails.billingConfiguration)"
        />
      </div>

      <div id="section-traffic">
        <AccountTrafficDetails
          v-if="sellerAccountDetails"
          :account="sellerAccountDetails"
          type="seller"
        />
      </div>

      <div id="section-bid-settings">
        <SellerBidSettings
          :account-id="getSafe(() => sellerAccountDetails.id)"
          :existing-bid-preferences="getSafe(() => sellerAccountDetails.bidPreferences)"
        />
      </div>

      <CCard
        v-if="sellerAccountDetails"
        id="section-approved-offers"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('approvedOffers')"
        >
          <CIcon
            name="cil-check-circle"
            class="mr-2"
          />
          <strong>Approved Offers</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.approvedOffers.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.approvedOffers.expanded">
          <CCardBody>
            <SellerAccountApprovedOffers
              ref="approvedOffers"
              :is-loading="isLoading || loadingOffersRequests"
              :lazy-load="true"
              @reload="reloadApprovedOffers"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <CCard
        v-if="sellerAccountDetails"
        id="section-missing-offers"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('missingOffers')"
        >
          <CIcon
            name="cil-ban"
            class="mr-2"
          />
          <strong>Missing Offers</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.missingOffers.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.missingOffers.expanded">
          <CCardBody>
            <SellerAccountMissingOffers
              ref="missingOffers"
              :account-id="sellerAccountDetails.id"
              :lazy-load="true"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <CCard
        v-if="sellerAccountDetails"
        id="section-users"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('users')"
        >
          <CIcon
            name="cil-people"
            class="mr-2"
          />
          <strong>Users</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.users.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.users.expanded">
          <CCardBody>
            <AccountUsers
              ref="accountUsers"
              :account-id="getSafe(() => sellerAccountDetails.id)"
              account-type="Seller"
              :account-name="sellerAccountDetails.name"
              :lazy-load="true"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <CCard
        v-if="sellerAccountDetails"
        id="section-creatives"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('creatives')"
        >
          <CIcon
            name="cil-image"
            class="mr-2"
          />
          <strong>Creatives</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.creatives.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.creatives.expanded">
          <CCardBody>
            <SellerCreatives
              ref="sellerCreatives"
              :account-id="getSafe(() => sellerAccountDetails.id)"
              :lazy-load="true"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <CCard
        v-if="sellerAccountDetails"
        id="section-notes"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('notes')"
        >
          <CIcon
            name="cil-notes"
            class="mr-2"
          />
          <strong>Notes</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.notes.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.notes.expanded">
          <CCardBody>
            <ReviewNotes
              ref="reviewNotes"
              :parent-object="sellerAccountDetails"
              :lazy-load="true"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <CCard
        v-if="sellerAccountDetails && sellerAccountDetails.registrationId"
        id="section-registration-checklist"
      >
        <CCardHeader
          class="clickable d-flex align-items-center"
          @click="toggleSection('checklist')"
        >
          <CIcon
            name="cil-check-circle"
            class="mr-2"
          />
          <strong>Review Checklist</strong>
          <CIcon
            class="ml-auto"
            :name="collapsibleSections.checklist.expanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
          />
        </CCardHeader>
        <CCollapse :show="collapsibleSections.checklist.expanded">
          <CCardBody>
            <RegistrationRequestChecklist
              ref="registrationChecklist"
              :registration-request-id="sellerAccountDetails.registrationId"
              :account-id="accountId"
              :next-pending-item-for-final-approval="sellerAccountDetails.nextPendingItemForFinalApproval"
              type="seller"
              :lazy-load="true"
              @pending-status-changed="handlePendingStatusChanged"
            />
          </CCardBody>
        </CCollapse>
      </CCard>

      <div
        v-if="isAdminApprovalRequirementNotMet && sellerAccountDetails"
        class="d-flex align-items-center mb-3"
      >
        <CButton
          type="submit"
          size="lg"
          color="success"
          class="mr-3"
          @click="warningApproveModal = true"
        >
          Final Approve
        </CButton>

        <div
          v-if="sellerAccountDetails.readyForOnboarding"
          class="alert alert-success d-inline-flex align-items-center mb-0 py-1 px-2 small"
        >
          <CIcon
            name="cil-check-circle"
            class="mr-2"
          />
          Ready for Onboarding
          <CButton
            color="light"
            size="sm"
            class="ml-3"
            :disabled="isReadyForOnboardingLoading"
            @click="setReadyForOnboarding(false)"
          >
            Undo
          </CButton>
        </div>
        <CButton
          v-else
          size="lg"
          color="info"
          :disabled="isReadyForOnboardingLoading"
          @click="setReadyForOnboarding(true)"
        >
          Move to Ready for Onboarding
        </CButton>
      </div>

      <CModal
        title="Confirm approval"
        color="success"
        :show.sync="warningApproveModal"
      >
        Are you sure you want to approve this seller?

        <template #footer>
          <ButtonLoading
            color="primary"
            :loading="isLoading"
            @click="approveAccount"
          >
            Approve
          </ButtonLoading>

          <CButton
            color="danger"
            @click="warningApproveModal = false"
          >
            Cancel
          </CButton>
        </template>
      </CModal>

      <CModal
        title="Confirm disable"
        color="warning"
        :show.sync="disableAccountModal"
      >
        Are you sure you want to {{ selectedDisableReason === 'Paused' ? 'pause' : selectedDisableReason === 'Deactivated' ? 'deactivate' : 'ban' }} this account?

        <CTextarea
          v-model="remarks"
          class="mt-2"
          name="Remarks"
          label="Remarks"
          :is-valid="areRemarksValid"
          invalid-feedback="Remarks are mandatory for disabling"
          small
          required
          rows="3"
          :debounce="500"
          description="Remarks will be sent in the email notification to the client"
        />

        <CTextarea
          v-if="sellerAccountDetails !== null"
          v-model="accountNameNote"
          class="mt-2"
          name="AccountNameNote"
          :label="'Enter account name for confirmation - ' + sellerAccountDetails.name"
          :is-valid="isAccountNameInputAsExpected(sellerAccountDetails)"
          invalid-feedback="Enter account name (company name) to confirm account disabling"
          small
          required
          rows="1"
          :debounce="500"
        />

        <template #footer>
          <ButtonLoading
            color="primary"
            :loading="isDisableEnableLoading"
            :disabled="!isAccountNameInputAsExpected(sellerAccountDetails) || !areRemarksValid"
            @click="confirmDisableAccount"
          >
            Disable
          </ButtonLoading>

          <CButton
            color="danger"
            @click="disableAccountModal = false; accountNameNote = ''; remarks = '';"
          >
            Cancel
          </CButton>
        </template>
      </CModal>

      <CModal
        title="Confirm enable"
        color="success"
        :show.sync="enableAccountModal"
      >
        Are you sure you want to enable this account?

        <CTextarea
          v-model="remarks"
          class="mt-2"
          name="Remarks"
          label="Remarks"
          :is-valid="areRemarksValid"
          invalid-feedback="Remarks are mandatory for enabling"
          small
          required
          rows="3"
          :debounce="500"
          description="Remarks will be sent in the email notification to the client"
        />

        <CTextarea
          v-if="sellerAccountDetails !== null"
          v-model="accountNameNote"
          class="mt-2"
          name="AccountNameNote"
          :label="'Enter account name for confirmation - ' + sellerAccountDetails.name"
          :is-valid="isAccountNameInputAsExpected(sellerAccountDetails)"
          invalid-feedback="Enter account name (company name) to confirm account enabling"
          small
          required
          rows="1"
          :debounce="500"
        />

        <template #footer>
          <ButtonLoading
            color="primary"
            :loading="isDisableEnableLoading"
            :disabled="!isAccountNameInputAsExpected(sellerAccountDetails) || !areRemarksValid"
            @click="confirmEnableAccount"
          >
            Enable
          </ButtonLoading>

          <CButton
            color="danger"
            @click="enableAccountModal = false; accountNameNote = ''; remarks = '';"
          >
            Cancel
          </CButton>
        </template>
      </CModal>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import isEmpty from 'lodash.isempty'

import SellerBillingDetails from './SellerBillingDetails.vue'
import AccountTrafficDetails from './AccountTrafficDetails.vue'
import SignedDocumentBundles from './SignedDocumentBundles.vue'
import AccountRequirements from './AccountRequirements.vue'
import AccountDetailsCommonInfoReadOnly from './AccountDetailsCommonInfoReadOnly.vue'
import AccountDetailsCommonInfoEdit from './AccountDetailsCommonInfoEdit.vue'
import SellerBankInfo from './SellerBankInfo.vue'
import ReviewNotes from '../ReviewNotes/ReviewNotes.vue'
import SellerBidSettings from './SellerBidSettings.vue'
import AccountUsers from '@/views/Users/AccountUsers.vue'
import SellerAccountApprovedOffers from './SellerAccountApprovedOffers.vue'
import SellerAccountMissingOffers from './SellerAccountMissingOffers.vue'
import RegistrationRequestChecklist from '@/views/RegistrationRequests/RegistrationRequestChecklist.vue'
import SellerCreatives from '@/views/Creatives/SellerCreatives.vue'

import { showSuccessMessage, showErrorMessage } from '@/notification-utils'
import { COMMON_ACCOUNT_REQUIREMENTS } from '@/store/modules/accounts.store'
import hashNavigationMixin from '@/mixins/hash-navigation-mixin'

export default {
  name: 'SellerAccountDetails',

  components: {
    SellerBillingDetails,
    ReviewNotes,
    AccountTrafficDetails,
    SignedDocumentBundles,
    AccountUsers,
    AccountDetailsCommonInfoReadOnly,
    AccountDetailsCommonInfoEdit,
    SellerBankInfo,
    SellerBidSettings,
    RegistrationRequestChecklist,
    AccountRequirements,
    SellerAccountApprovedOffers,
    SellerAccountMissingOffers,
    SellerCreatives
  },

  mixins: [hashNavigationMixin],

  data() {
    return  {
      editMode: false,
      isLoading: false,
      isReadyForOnboardingLoading: false,
      warningApproveModal: false,
      COMMON_ACCOUNT_REQUIREMENTS,
      loadingOffersRequests: false,
      disableAccountModal: false,
      enableAccountModal: false,
      isDisableEnableLoading: false,
      accountNameNote: '',
      remarks: '',
      selectedDisableReason: '',
      accountAction: '',
      // Collapsible sections state
      collapsibleSections: {
        approvedOffers: { expanded: false, loaded: false },
        missingOffers: { expanded: false, loaded: false },
        users: { expanded: false, loaded: false },
        creatives: { expanded: false, loaded: false },
        notes: { expanded: false, loaded: false },
        checklist: { expanded: false, loaded: false }
      }
    }
  },

  computed: {
    ...mapState('accounts', [
      'sellerAccountDetails',
      'sellerAccountRequirements'
    ]),

    ...mapState('category', [
      'categories'
    ]),

    ...mapGetters('trafficSource', {
      trafficSources: 'activeTrafficSources'
    }),

    ...mapGetters('timezones', [
      'timezonesMap'
    ]),

    categoriesMap() {
      return this.categories.reduce(function(map, obj) {
        map[obj.id] = obj
        return map
      }, {})
    },

    trafficSourcesMap() {
      return this.trafficSources.reduce(function(map, obj) {
        map[obj.id] = obj
        return map
      }, {})
    },

    signedBundles() {
      const bundles = this.sellerAccountDetails?.eSignature?.signedBundles ?? []
      const bundleIds = this.sellerAccountDetails?.eSignature?.signedBundleIds?.map(b => ({ id: b })) ?? []

      return bundles.concat(bundleIds)
    },

    isAdminApprovalRequirementNotMet() {
      return !isEmpty(this.sellerAccountRequirements?.requirements) && !this.sellerAccountRequirements?.requirements[COMMON_ACCOUNT_REQUIREMENTS.ADMIN_APPROVAL_REQUIREMENT]
    },

    navigationItems() {
      const items = [
        { id: 'section-account-details', label: 'Account Details', icon: 'cil-user' },
        { id: 'section-billing', label: 'Account Billing', icon: 'cil-dollar' },
        { id: 'section-traffic', label: 'Traffic Details', icon: 'cil-transfer' },
        { id: 'section-bid-settings', label: 'Bid Settings', icon: 'cil-settings' },
        { id: 'section-approved-offers', label: 'Approved Offers', icon: 'cil-check-circle', collapsibleKey: 'approvedOffers' },
        { id: 'section-missing-offers', label: 'Missing Offers', icon: 'cil-ban', collapsibleKey: 'missingOffers' },
        { id: 'section-users', label: 'Users', icon: 'cil-people', collapsibleKey: 'users' },
        { id: 'section-creatives', label: 'Creatives', icon: 'cil-image', collapsibleKey: 'creatives' },
        { id: 'section-notes', label: 'Notes', icon: 'cil-notes', collapsibleKey: 'notes' }
      ]

      if (this.sellerAccountDetails?.registrationId) {
        items.push({ id: 'section-registration-checklist', label: 'Review Checklist', icon: 'cil-check-circle', collapsibleKey: 'checklist' })
      }

      return items
    }
  },

  async created() {
    this.accountId = this.$route.params.id

    if (this.accountId) {
      try {
        await this.getSellerAccountDetails(this.accountId)
        await this.getSellerAccountRequirements(this.accountId)
        document.title = this.sellerAccountDetails?.name
      } catch (error) {
        // Exception handled in interceptor
      }
    }

    await this.getCategories()
    await this.getTrafficSources()

    this.SET_CURRENT_REGISTRATION_TYPE('seller')

    // Trigger hash navigation after data is loaded so sections are rendered
    this.$nextTick(() => {
      this.handleHashNavigation()
    })
  },

  methods: {
    ...mapActions('accounts', [
      'getSellerAccountDetails',
      'getSellerAccountRequirements',
      'markAccountAdminApprovalRequirement',
      'markAccountReadyForOnboarding',
      'getPendingSellerAccounts',
      'getApprovedSellerOfferRequestsForAccount',
      'disableAccount',
      'enableAccount'
    ]),

    ...mapActions('category', {
      getCategories: 'list'
    }),

    ...mapActions('trafficSource', {
      getTrafficSources: 'list'
    }),

    ...mapMutations('registrationRequests', [
      'SET_CURRENT_REGISTRATION_TYPE'
    ]),

    async handlePendingStatusChanged() {
      await this.getSellerAccountDetails(this.accountId)
    },

    async setReadyForOnboarding(value) {
      try {
        this.isReadyForOnboardingLoading = true
        await this.markAccountReadyForOnboarding({
          accountId: this.sellerAccountDetails.id,
          accountType: 'seller',
          value
        })
        await this.getSellerAccountDetails(this.accountId)
        await this.getPendingSellerAccounts()
        showSuccessMessage(value ? 'Account marked as Ready for Onboarding' : 'Ready for Onboarding status removed')
      } catch (error) {
        showErrorMessage('Failed to update Ready for Onboarding status')
      } finally {
        this.isReadyForOnboardingLoading = false
      }
    },

    toggleEditMode() {
      this.editMode = !this.editMode
    },

    async approveAccount() {
      try {
        this.isLoading = true

        await this.markAccountAdminApprovalRequirement({
          accountId: this.sellerAccountDetails.id,
          approve: true,
          accountType: 'seller'
        })

        showSuccessMessage('Admin Approval done successfully')

        this.warningApproveModal = false

        await this.getSellerAccountRequirements(this.accountId)
      } catch (error) {
        //
      } finally {
        this.isLoading = false
      }
    },

    async reloadApprovedOffers() {
      try {
        this.loadingOffersRequests = true

        await this.getApprovedSellerOfferRequestsForAccount(this.accountId)
      } catch (error) {
        //
      } finally {
        this.loadingOffersRequests = false
      }
    },

    showDisableAccountModal(disableReason) {
      this.accountAction = 'disable'
      this.selectedDisableReason = disableReason
      this.disableAccountModal = true
    },

    showEnableAccountModal() {
      this.accountAction = 'enable'
      this.enableAccountModal = true
    },

    async confirmDisableAccount() {
      try {
        this.isDisableEnableLoading = true
        await this.disableAccount({
          accountId: this.sellerAccountDetails.id,
          type: 'seller',
          remarks: this.remarks,
          disableReason: this.selectedDisableReason
        })

        showSuccessMessage('Successfully disabled account ' + this.sellerAccountDetails.id)

        await this.getSellerAccountDetails(this.accountId)
      } catch (error) {
        //
      } finally {
        this.disableAccountModal = false
        this.isDisableEnableLoading = false
        this.accountNameNote = ''
        this.remarks = ''
        this.selectedDisableReason = ''
      }
    },

    async confirmEnableAccount() {
      try {
        this.isDisableEnableLoading = true
        await this.enableAccount({
          accountId: this.sellerAccountDetails.id,
          type: 'seller',
          remarks: this.remarks
        })

        showSuccessMessage('Successfully enabled account ' + this.sellerAccountDetails.id)

        await this.getSellerAccountDetails(this.accountId)
      } catch (error) {
        //
      } finally {
        this.enableAccountModal = false
        this.isDisableEnableLoading = false
        this.accountNameNote = ''
        this.remarks = ''
      }
    },

    isAccountNameInputAsExpected(account) {
      if (this.accountAction === 'enable' && this.accountNameNote === account.name) {
        return true
      }

      if (this.accountAction === 'disable' && this.accountNameNote === account.name) {
        return true
      }

      return false
    },

    areRemarksValid() {
      return !!this.remarks.length
    },

    getDisableReasonBadgeColor(disableReason) {
      switch (disableReason) {
        case 'Paused':
          return 'warning'
        case 'Deactivated':
          return 'dark'
        case 'Banned':
          return 'danger'
        default:
          return 'secondary'
      }
    },

    async toggleSection(sectionName) {
      const section = this.collapsibleSections[sectionName]
      section.expanded = !section.expanded

      if (section.expanded && !section.loaded) {
        await this.loadSectionData(sectionName)
        section.loaded = true
      }
    },

    async loadSectionData(sectionName) {
      switch (sectionName) {
        case 'approvedOffers':
          await this.reloadApprovedOffers()
          break
        case 'missingOffers':
          this.$refs.missingOffers?.loadData()
          break
        case 'users':
          this.$refs.accountUsers?.loadUsers()
          break
        case 'creatives':
          this.$refs.sellerCreatives?.loadCreatives()
          break
        case 'notes':
          this.$refs.reviewNotes?.fetchNotes()
          break
        case 'checklist':
          this.$refs.registrationChecklist?.loadData()
          break
      }
    },

    getCollapsibleKeyForSection(sectionId) {
      const navItem = this.navigationItems.find(item => item.id === sectionId)
      return navItem?.collapsibleKey
    },

    async expandSection(collapsibleKey) {
      if (this.collapsibleSections[collapsibleKey] && !this.collapsibleSections[collapsibleKey].expanded) {
        await this.toggleSection(collapsibleKey)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.seller-account-details-wrapper {
  position: relative;
  margin-left: -0.75rem;
  margin-right: -0.75rem;
}

.seller-account-details-content {
  margin-right: 145px;

  @media (max-width: 1200px) {
    margin-right: 0;
  }
}

.section-nav {
  position: fixed;
  top: 120px;
  right: 18px;
  width: 125px;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;

  @media (max-width: 1200px) {
    display: none;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 4px 0;
  }

  &__item {
    margin: 0;
  }

  &__link {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    color: #4f5d73;
    font-size: 11px;
    text-decoration: none;
    transition: background-color 0.15s, color 0.15s;

    &:hover {
      background-color: #ebedef;
      color: #321fdb;
      text-decoration: none;
    }
  }
}

.clickable {
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}
</style>